import { Checkbox, Collapse, Divider, Tooltip } from '@mui/material'
import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import NewButton from 'ui/atoms/NewButton/NewButton'
import NewIcon from 'ui/atoms/NewIcon/NewIcon'

type SettingPrioritySelectorProps = {
  priorities: SystemSettingPriorityLevel[]
  selectedPriorityLevels: number[]
  onChange: (levels: number[]) => void
}

export default function SettingPrioritySelector({
  priorities,
  selectedPriorityLevels,
  onChange,
}: SettingPrioritySelectorProps): ReactElement {
  const { t } = useTranslation()
  const dropdownRef = useRef<HTMLDivElement>(null)

  const visiblePriorityLevelSet = useMemo(() => {
    return new Set(
      priorities.filter((priority) => priority.is_hidden !== true).map((priority) => priority.priority_level)
    )
  }, [priorities])

  const visiblePriorities = useMemo(
    () => priorities.filter((priority) => visiblePriorityLevelSet.has(priority.priority_level)),
    [priorities, visiblePriorityLevelSet]
  )

  const visibleSelectedPriorityLevels = useMemo(
    () => selectedPriorityLevels.filter((level) => visiblePriorityLevelSet.has(level)),
    [selectedPriorityLevels, visiblePriorityLevelSet]
  )

  const hiddenSelectedPriorityLevels = useMemo(
    () => selectedPriorityLevels.filter((level) => !visiblePriorityLevelSet.has(level)),
    [selectedPriorityLevels, visiblePriorityLevelSet]
  )

  const [expanded, setExpanded] = useState(false)
  const [checkedPriorities, setCheckedPriorities] = useState<Set<number>>(new Set(visibleSelectedPriorityLevels))
  const [prevCheckedPriorities, setPrevCheckedPriorities] = useState<Set<number>>(
    new Set(visibleSelectedPriorityLevels)
  )

  // Sync internal state with external prop changes
  useEffect(() => {
    setCheckedPriorities(new Set(visibleSelectedPriorityLevels))
  }, [visibleSelectedPriorityLevels])

  const numberOfFiltersApplied = useMemo(() => {
    return visibleSelectedPriorityLevels.length
  }, [visibleSelectedPriorityLevels])

  const sortedPriorities = useMemo(
    () => [...visiblePriorities].sort((a, b) => a.priority_level - b.priority_level),
    [visiblePriorities]
  )

  const allChecked = useMemo(() => {
    return visiblePriorities.length > 0 && visiblePriorities.every((p) => checkedPriorities.has(p.priority_level))
  }, [visiblePriorities, checkedPriorities])

  const noChanges = useCallback(() => {
    if (checkedPriorities.size !== prevCheckedPriorities.size) {
      return false
    }
    for (const level of checkedPriorities) {
      if (!prevCheckedPriorities.has(level)) {
        return false
      }
    }
    return true
  }, [checkedPriorities, prevCheckedPriorities])

  const handleApply = useCallback(() => {
    if (!noChanges()) {
      onChange(Array.from(new Set([...Array.from(checkedPriorities), ...hiddenSelectedPriorityLevels])))
    }
    setExpanded(false)
  }, [checkedPriorities, hiddenSelectedPriorityLevels, noChanges, onChange])

  const handleShowAll = useCallback(() => {
    setCheckedPriorities(new Set(visiblePriorities.map((p) => p.priority_level)))
  }, [visiblePriorities])

  const handleSelectAll = useCallback(() => {
    if (allChecked) {
      setCheckedPriorities(new Set())
    } else {
      setCheckedPriorities(new Set(visiblePriorities.map((p) => p.priority_level)))
    }
  }, [allChecked, visiblePriorities])

  const togglePriority = useCallback((level: number) => {
    setCheckedPriorities((prev) => {
      const next = new Set(prev)
      if (next.has(level)) {
        next.delete(level)
      } else {
        next.add(level)
      }
      return next
    })
  }, [])

  const handleToggleDropdown = useCallback(() => {
    if (expanded) {
      // Closing without applying: reset to last applied state
      setCheckedPriorities(new Set(visibleSelectedPriorityLevels))
    } else {
      setPrevCheckedPriorities(new Set(checkedPriorities))
    }

    setExpanded(!expanded)
  }, [expanded, visibleSelectedPriorityLevels, checkedPriorities])

  // Handle click outside
  useEffect(() => {
    if (!expanded) return

    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        handleApply()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [expanded, handleApply])

  useEffect(() => {
    if (!expanded) return

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        handleApply()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [expanded, handleApply])

  return (
    <div ref={dropdownRef} className="relative self-center">
      <button
        type="button"
        aria-expanded={expanded}
        className={`flex flex-row items-center justify-between border-gray-300 w-62 h-10 border ${expanded ? 'rounded-t-md border-b-0' : 'rounded-md'} p-2 cursor-pointer bg-white`}
        onClick={handleToggleDropdown}
      >
        <div className="mx-1 flex flex-row justify-between w-full">
          <span>{t('Deviation type')}</span>
          <div className="flex gap-1">
            {numberOfFiltersApplied > 0 && !allChecked && (
              <Tooltip title={t('{{count}} selected', { count: numberOfFiltersApplied })} arrow placement="top">
                <span
                  className={`border border-vercel-primary rounded-md ${numberOfFiltersApplied > 9 ? 'px-1' : 'px-1.5'} text-vercel-primary`}
                >
                  {numberOfFiltersApplied}
                </span>
              </Tooltip>
            )}
          </div>
        </div>
        <NewIcon icon={expanded ? 'fal fa-chevron-down' : 'fal fa-chevron-right'} color="black" ariaLabel="expand" />
      </button>

      {/* Dropdown content */}
      <Collapse in={expanded} timeout="auto">
        <div className="flex flex-col w-62 bg-white border border-gray-300 rounded-b-md border-t-0 p-2 absolute z-10">
          <div className="flex flex-col">
            <div
              role="checkbox"
              tabIndex={0}
              aria-checked={allChecked}
              className="flex flex-row items-center justify-between rounded-md pl-1.5 my-1 hover:bg-gray-100 hover:cursor-pointer"
              onClick={handleSelectAll}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  handleSelectAll()
                }
              }}
            >
              {t('Select all')}
              <Checkbox checked={allChecked} onChange={handleSelectAll} onClick={(e) => e.stopPropagation()} />
            </div>

            <Divider />

            {/* Priority options */}
            <div className="overflow-y-auto grow max-h-[40vh]">
              {sortedPriorities.map((priority) => (
                <div
                  key={priority.priority_level}
                  role="checkbox"
                  tabIndex={0}
                  aria-checked={checkedPriorities.has(priority.priority_level)}
                  className="flex flex-row items-center justify-between rounded-md pl-1.5 my-1 hover:bg-gray-100 hover:cursor-pointer"
                  onClick={() => togglePriority(priority.priority_level)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      togglePriority(priority.priority_level)
                    }
                  }}
                >
                  <span className={`truncate ${checkedPriorities.has(priority.priority_level) ? '' : 'opacity-50'}`}>
                    {t(priority.display_name)}
                  </span>
                  <Checkbox
                    checked={checkedPriorities.has(priority.priority_level)}
                    onClick={(e) => {
                      e.stopPropagation()
                      togglePriority(priority.priority_level)
                    }}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex flex-row items-center justify-between bg-white sticky bottom-0 pt-2 border-t border-gray-300">
            <NewButton variant="outlined" label={t('Show all')} onClick={handleShowAll} disabled={allChecked} />
            <NewButton label={t('Apply')} onClick={handleApply} />
          </div>
        </div>
      </Collapse>
    </div>
  )
}
